import { z } from 'zod';

/**
 * Cross-cutting primitives reused by resource schemas. Keeping these in one
 * place means field-level rules (id format, timestamp format, pagination
 * bounds) are defined once and stay consistent across the API.
 */

/** A v4 UUID string — the id shape used for all resources. */
export const idSchema = z.uuid();

/** ISO-8601 timestamp string (what D1 stores and the API returns). */
export const timestampSchema = z.iso.datetime();

/** Standard error envelope returned by the API on any failure. */
export const errorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    /** Optional structured detail, e.g. flattened Zod validation issues. */
    details: z.unknown().optional(),
  }),
});
export type ErrorResponse = z.infer<typeof errorResponseSchema>;

/**
 * Pagination query params. `z.coerce` turns the raw string query values into
 * numbers before validation, so handlers get real numbers with safe bounds.
 */
export const paginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

/** Wraps a list payload with the pagination metadata the client needs. */
export function paginatedSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number().int().min(0),
    limit: z.number().int(),
    offset: z.number().int(),
  });
}
